<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { FilterModal, NumberStepperInput, SearchCombobox, ThemeProvider } from '@/shared/ui';
  import { formatDateForApi, parseCivilDate } from '@/shared/lib';
  import { MAX_REJECTED_FILTER_INTEGER } from '../lib/rejected-list-query';
  import { useMemberFilterOptions } from '../model/useMemberFilterOptions';
  import FilterDateField from './FilterDateField.vue';
  import type { RejectedListFilters } from '../lib/rejected-list-query';

  const props = defineProps<{
    currentFilters?: RejectedListFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: RejectedListFilters];
    'clear-filters': [];
  }>();

  const draftDate = ref<Date | null>(null);
  const draftCreatedBy = ref('');
  const draftRevision = ref<number | null>(null);

  const {
    options: memberComboboxOptions,
    isLoading: isMembersLoading,
    searchQuery: memberSearchQuery,
  } = useMemberFilterOptions();

  const loadFromFilters = (filters?: RejectedListFilters) => {
    draftDate.value = parseCivilDate(filters?.rejectedAt);
    draftCreatedBy.value = filters?.createdBy ? String(filters.createdBy) : '';
    draftRevision.value = filters?.revision ?? null;
  };

  loadFromFilters(props.currentFilters);

  watch(() => props.currentFilters, loadFromFilters, { deep: true });

  const updateRevision = (value: number | null) => {
    draftRevision.value = value === null ? null : Math.max(1, Math.trunc(value));
  };

  const handleApply = () => {
    emit('update:filter', {
      rejectedAt: draftDate.value ? (formatDateForApi(draftDate.value) ?? undefined) : undefined,
      createdBy: draftCreatedBy.value ? Number(draftCreatedBy.value) : undefined,
      revision: draftRevision.value ?? undefined,
    });
  };

  const handleClear = () => {
    draftDate.value = null;
    draftCreatedBy.value = '';
    draftRevision.value = null;
    memberSearchQuery.value = '';
    emit('clear-filters');
  };

  const handleClose = () => {
    loadFromFilters(props.currentFilters);
    emit('close');
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-sm"
    @close="handleClose"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider>
        <div class="flex flex-col gap-4 px-6 py-5">
          <FilterDateField v-model="draftDate" trigger-test-id="rejected-filter-date-trigger" />

          <div class="flex flex-col gap-1.5">
            <h4 class="text-sm font-medium text-gray-07">Elaborador</h4>
            <SearchCombobox
              :model-value="draftCreatedBy"
              :options="memberComboboxOptions"
              :loading="isMembersLoading"
              placeholder="Selecione um colaborador"
              search-placeholder="Buscar colaborador..."
              trigger-icon="arrow-down"
              class="w-full h-11"
              data-testid="rejected-filter-creator-combobox"
              @update:model-value="draftCreatedBy = $event || ''"
              @search="memberSearchQuery = $event"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <h4 id="rejected-filter-revision-label" class="text-sm font-medium text-gray-07">
              Revisão
            </h4>
            <NumberStepperInput
              :model-value="draftRevision"
              :min="1"
              :max="MAX_REJECTED_FILTER_INTEGER"
              placeholder="0"
              aria-labelledby="rejected-filter-revision-label"
              wrapper-class="w-36"
              input-class="w-full bg-white-01"
              data-testid="rejected-filter-revision-input"
              @update:model-value="updateRevision"
            />
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
